import { FC, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Data } from '@pcmp/react-coworker-components';

import { GqlError } from '@/typings/graphql';
import Button from '@ingka/button';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const { Card } = Data;

const GenericError: FC<{
  className?: string;
  error?:
    | FetchBaseQueryError
    | GqlError
    | Error
    | { name?: string; message?: string };
  notify?: boolean;
  onClick?: () => void;
}> = ({ error = {}, notify, className, onClick }) => {
  const { t } = useTranslation();
  const errorName = 'name' in error ? error.name : 'GenericError';
  const errorMessage =
    'message' in error ? error.message : 'An unexpected error occurred';

  useEffect(() => {
    const shouldNotify = notify || (error as any)?.['notifySentry'];

    shouldNotify && Sentry.captureException(error);
  }, [notify, error]);

  return (
    <div data-testid="genericError" className={className}>
      <h2 className="text-lg pb-3 text-center">{t('error.generic.title')}</h2>
      <p className="text-justify">{t('error.generic.description')}</p>

      <p className="py-6 text-gray-600 font-mono">
        {errorName}: {errorMessage}
      </p>

      <div className="flex flex-row justify-between">
        <div className="flex space-x-2 items-end">
          <div>
            <img src="/slack.svg" width={20} height={20} alt="Slack logo" />
          </div>
          <a
            href="https://ikea.enterprise.slack.com/archives/C073L4GTJ12"
            target="_blank"
          >
            #checka-pub
          </a>
        </div>
        {onClick && (
          <Button size="small" type="primary" onClick={onClick}>
            {t('error.generic.retry')}
          </Button>
        )}
      </div>
    </div>
  );
};

export const GenericErrorCard: FC<{
  className?: string;
  error?:
    | FetchBaseQueryError
    | GqlError
    | Error
    | { name?: string; message?: string };
  notify?: boolean;
  card?: boolean;
  onClick?: () => void;
}> = ({ className, card, ...rest }) => {
  const cardStyles = card
    ? 'bg-white rounded-lg shadow-lg border border-gray-100'
    : 'bg-transparent';

  return (
    <div className={`${className} w-full`}>
      <Card
        className={`${cardStyles} flex flex-col items-center md:max-w-[50%] my-0 mx-auto p-6`}
      >
        <GenericError {...rest} />
      </Card>
    </div>
  );
};
